<template>
  <v-card class="my-3">

    <confirm-dialog
      :is-loading="isLoading"
      :is-delete="true"
      :is-open="inactivityDeleteDialog"
      :dialog-header="$t('deleteHeader')"
      :dialog-body="$t('deleteBody')"
      :confirm-button-text="$t('delete')"
      @confirm="deleteEntry"
      @dialogToggle="val => inactivityDeleteDialog = val"
    />

    <v-toolbar
      flat
      color="white">
      <v-toolbar-title>{{ $t('heading', {user: `${currentEmployee.first_name} ${currentEmployee.last_name}`}) }}
      </v-toolbar-title>
    </v-toolbar>

    <v-data-table
      v-if="inactivity"
      :headers="headers"
      :items="inactivity"
      :total-items="paginationTotal"
      :rows-per-page-items="[15]"
      :pagination.sync="pagination"
      :loading="isTableLoading">
      <template
        slot="items"
        slot-scope="{item}">
        <td>{{ toDate(item.begin) }} - {{ toDate(item.end) }} <v-btn
          v-if="!item.end"
          icon
          @click="openClosePeriodDialog(item.id)"><v-icon>close</v-icon></v-btn> </td>
        <td>{{ item.comment }}</td>
        <td>{{ item.author.first_name }} {{ item.author.last_name }}</td>
        <td class="text-xs-right">
          <v-btn
            icon
            @click="openDialog(item.id)">
            <v-icon>delete</v-icon>
          </v-btn>
        </td>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialog"
      width="290"
    >

      <v-date-picker
        v-model="picker"
        :allowed-dates="allowedEndDates"
        :locale="locale"
        flat/>

      <v-flex
        style="background: white"
        class="text-xs-center">
        <v-btn
          :disabled="isLoading"
          :loading="isLoading"
          color="primary"
          class="ma-3"
          @click="closePeriod()"
        >{{ $t('closePeriod') }}</v-btn>
      </v-flex>
    </v-dialog>

    <employee-inactivity-entry-dialog
      v-model="createEntryDialog"
      @updateOnSuccess="updateOnSuccess"/>

    <v-card-actions>
      <v-spacer/>
      <v-btn
        color="primary"
        class="ml-2"
        @click="createEntryDialog = true">
        {{ $t('createEntry') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import CompanyApi from '@/api/CompanyApi'
import EmployeeInactivityEntryDialog from './EmployeeInactivityEntryDialog'
import PaginationApi from '@/api/PaginationApi'
import ConfirmDialog from '@/components/global-components/confirmDialog/ConfirmDialog'

export default {
  name: 'EmployeeInactivityTable',
  components: { ConfirmDialog, EmployeeInactivityEntryDialog },
  data () {
    return {
      isTableLoading: false,
      isLoading: false,
      pagination: {},
      dialog: false,
      paginationTotal: 0,
      createEntryDialog: false,
      inactivityDeleteDialog: false,
      inactivity: [],
      picker: null,
      currentInactivityPeriodId: null,
      headers: [
        { text: this.$t('dateRange'), sortable: false },
        { text: this.$t('comment'), sortable: false },
        { text: this.$t('settingManager'), sortable: false },
        { text: '', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters('employee', ['currentEmployee']),
    periodsUrlByRole () {
      return `company/employees/${this.currentEmployee.id}/periods`
    },
    locale () {
      return `${localStorage.locale}-${localStorage.locale}`
    }
  },
  methods: {
    updateOnSuccess () {
      this.$emit('updateExcludedMonths')
      return this.getPaginatedData()
    },
    openDialog (id) {
      this.inactivityDeleteDialog = true
      this.currentInactivityPeriodId = id
    },

    openClosePeriodDialog (id) {
      this.dialog = true
      this.currentInactivityPeriodId = id
    },

    closeDialog () {
      this.inactivityDeleteDialog = false
      this.currentInactivityPeriodId = null
    },
    async deleteEntry () {
      this.isLoading = true
      try {
        await this.deleteEntryByRole()
        await this.updateOnSuccess()
        this.closeDialog()
      } catch (e) {
        this.$notification(e)
      }
      this.isLoading = false
    },

    async closePeriod () {
      const end = this.$moment(this.picker, 'YYYY-MM-DD').format('DD-MM-YYYY')
      this.isLoading = true
      try {
        const response = await this.closePeriodByRole(this.currentInactivityPeriodId, end)
        await this.updateOnSuccess()
        this.$notification(response)
      } catch (e) {
        this.$notification(e)
      }
      this.isLoading = false
      this.dialog = false
    },

    deleteEntryByRole () {
      return CompanyApi.deleteEmployeeInactivityPeriod(this.currentEmployee.id, this.currentInactivityPeriodId)
    },

    closePeriodByRole (period, end) {
      if (this.$can('close', 'AllPeriods')) {
        return CompanyApi.closeOpenPeriod(this.currentEmployee.id, period, end)
      }
    },

    allowedEndDates (val) {
      const $date = this.$moment(val, 'YYYY-MM-DD')
      const $now = this.$moment()
      const isInPast = $date.diff($now, 'days') <= 0
      let isAfterBegin = true

      let beginDate = null

      if (this.inactivity.length && this.currentInactivityPeriodId) {
        beginDate = this.$moment(this.inactivity.filter(item => item.id === this.currentInactivityPeriodId)[0].begin)
      }

      if (beginDate) {
        isAfterBegin = beginDate.diff($date, 'days') < 0
      }
      return isInPast && isAfterBegin
    },

    toDate (value) {
      return value ? this.$moment(value, 'YYYY-MM-DD hh:mm:ss')
        .format('DD.MM.YYYY') : this.$t('openDate')
    },
    async getPaginatedData () {
      this.isTableLoading = true
      try {
        const response = await PaginationApi.getPaginatedData(this.periodsUrlByRole, [`page=${this.pagination.page}`])
        this.paginationTotal = response.total
        this.inactivity = response.data
      } catch (e) {
        this.$notification(e)
      }
      this.isTableLoading = false
    }
  },
  i18n: {
    messages: {
      en: {
        comment: 'Comment',
        createEntry: 'Add inactivity',
        dateRange: 'Date',
        deleteBody: 'Do you really want to delete the Entry?',
        deleteHeader: 'Delete?',
        delete: 'Delete',
        heading: 'Inactivity of {user}',
        settingManager: 'Created by',
        openDate: 'unlimited',
        closePeriod: 'End inactivity'
      },
      tr: {
        comment: 'Yorum',
        createEntry: 'Pasif ekle',
        dateRange: 'Tarih',
        deleteBody: 'Kaydı silmek istediğine emin misiniz?',
        deleteHeader: 'Sil?',
        delete: 'Sil',
        heading: '{user} kullanıcısının pasifliği',
        settingManager: 'Tanımlayan',
        openDate: 'sınırsız',
        closePeriod: 'Hareketsizliği sonlandır'
      },
      de: {
        comment: 'Kommentar',
        createEntry: 'Inaktivität hinzufügen',
        dateRange: 'Datum',
        deleteBody: 'Wollen Sie diesen Eintrag wirklich löschen?',
        deleteHeader: 'Löschen?',
        delete: 'Entfernen',
        heading: 'Inaktivität von {user}',
        settingManager: 'Erstellt von',
        openDate: 'unbegrenzt',
        closePeriod: 'Inaktivität beenden'
      },
      ru: {
        comment: 'Комментарий',
        createEntry: 'Добавить период неактивности',
        dateRange: 'Дата',
        delete: 'Удалить',
        deleteBody: 'Вы действительно хотите удалить запись?',
        deleteHeader: 'Удалить?',
        heading: 'Неактивность пользователя {user}',
        settingManager: 'Создано',
        openDate: 'неограниченный',
        closePeriod: 'Закончить Неактивность'
      }
    }
  },
  watch: {
    pagination: {
      handler: `getPaginatedData`
    }
  }
}
</script>
